export type StepLevel = 'basic' | 'standard' | 'advanced' | 'premium';
export type FulfillmentChannel = 'easyShip' | 'fba';
export type FbaShippingZone = 'regional' | 'national';
export type DimensionUnit = 'cm' | 'inch';
export type PackageDimension = 'lengthCm' | 'widthCm' | 'heightCm';

export interface EasyShipRate {
  first500g: number;
  upTo1kg: number;
  upTo2kg: number;
}

export const EASY_SHIP_RATE_CARD = {
  effectiveDate: '2026-03-16',
  sourceUrl: 'https://sell.amazon.in/fees-and-pricing',
  lowPriceDiscount: 10,
  lowPriceMaximum: 300,
  additionalKgAfter2: 34,
  additionalKgAfter5: 18,
  rates: {
    basic: { first500g: 69, upTo1kg: 89, upTo2kg: 126 },
    standard: { first500g: 65, upTo1kg: 85, upTo2kg: 122 },
    advanced: { first500g: 63, upTo1kg: 83, upTo2kg: 120 },
    premium: { first500g: 63, upTo1kg: 83, upTo2kg: 120 },
  } satisfies Record<StepLevel, EasyShipRate>,
} as const;

export const AMAZON_FEE_GST_RATE = 0.18;
export const WALL_ART_PRODUCT_GST_RATE = 0.05;

export const FBA_RATE_CARD = {
  effectiveDate: '2026-03-16',
  sourceUrl: 'https://sell.amazon.in/fees-and-pricing',
  defaultPackagingWeightGrams: 100,
  storagePerCubicFootMonth: 50,
  pickPack: {
    upTo1kg: 17,
    additionalKgThrough5: 5,
    additional5kgAfter5: 2,
  },
  closing: {
    upTo300: 20,
    upTo500: 18,
    upTo1000: 27,
    above1000: 52,
  },
  rates: {
    basic: {
      regional: { first500g: 42, upTo1kg: 58, upTo2kg: 82 },
      national: { first500g: 69, upTo1kg: 89, upTo2kg: 126 },
    },
    standard: {
      regional: { first500g: 39, upTo1kg: 54, upTo2kg: 78 },
      national: { first500g: 65, upTo1kg: 85, upTo2kg: 122 },
    },
    advanced: {
      regional: { first500g: 37, upTo1kg: 52, upTo2kg: 76 },
      national: { first500g: 63, upTo1kg: 83, upTo2kg: 120 },
    },
    premium: {
      regional: { first500g: 37, upTo1kg: 52, upTo2kg: 76 },
      national: { first500g: 63, upTo1kg: 83, upTo2kg: 120 },
    },
  } satisfies Record<StepLevel, Record<FbaShippingZone, EasyShipRate>>,
  additionalKgAfter2: {
    regional: 24,
    national: 34,
  } satisfies Record<FbaShippingZone, number>,
  additionalKgAfter5: {
    regional: 13,
    national: 18,
  } satisfies Record<FbaShippingZone, number>,
} as const;

export const AMAZON_RETURN_FEE_CARD = {
  effectiveDate: '2025-11-15',
  sourceUrl:
    'https://sellercentral.amazon.in/seller-forums/discussions/t/87030785-767f-485e-925f-60f4faae5915?mons_sel_locale=en_IN',
  rates: {
    basic: { upTo300: 50, upTo500: 75, upTo1000: 100, above1000: 140 },
    standard: { upTo300: 45, upTo500: 70, upTo1000: 95, above1000: 130 },
    advanced: { upTo300: 40, upTo500: 65, upTo1000: 85, above1000: 110 },
    premium: { upTo300: 40, upTo500: 65, upTo1000: 85, above1000: 110 },
  } satisfies Record<
    StepLevel,
    { upTo300: number; upTo500: number; upTo1000: number; above1000: number }
  >,
} as const;

export const WALL_ART_FEE_CARD = {
  category: 'Wall Art',
  effectiveDate: '2026-03-16',
  sourceUrl: 'https://sell.amazon.in/fees-and-pricing',
  referral: {
    upTo1000: 0,
    above1000: 0.135,
  },
  easyShipClosing: {
    upTo300: 1,
    upTo500: 22,
    upTo1000: 45,
    above1000: 76,
  },
} as const;

export interface PackageMeasurements {
  actualWeightGrams: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface ContributionInput extends PackageMeasurements {
  fulfillmentChannel: FulfillmentChannel;
  stepLevel: StepLevel;
  sellingPrice: number;
  productCost: number;
  packagingCost: number;
  advertisingCost: number;
  otherCost: number;
  returnRatePercentage: number;
  fbaShippingZone: FbaShippingZone;
  fbaStorageDays: number;
  fbaInboundCost: number;
  fbaShipInOwnContainer: boolean;
}

export interface HeavyBulkyCheck {
  isHeavyBulky: boolean;
  reasons: string[];
  girthCm: number;
}

export interface ContributionEstimate {
  fulfillmentChannel: FulfillmentChannel;
  actualWeightKg: number;
  volumetricWeightKg: number;
  chargeableWeightKg: number;
  girthCm: number;
  weightSlab: string;
  easyShipBaseFee: number;
  easyShipDiscount: number;
  easyShipFee: number;
  referralPercentage: number;
  referralFee: number;
  closingFeeBand: string;
  closingFee: number;
  amazonFeeSubtotal: number;
  amazonFeeGst: number;
  amazonCashFees: number;
  amazonFeeGstUtilized: number;
  amazonFeeGstCarryForward: number;
  productGst: number;
  netProductRevenue: number;
  gstPayableAfterAmazonFeeItc: number;
  nonAmazonCosts: number;
  deliveredContribution: number;
  deliveredMargin: number;
  returnRatePercentage: number;
  refundFeeBand: string;
  refundFee: number;
  returnedOrderAmazonFeeSubtotal: number;
  returnedOrderAmazonFeeGst: number;
  returnedOrderAmazonFees: number;
  returnedOrderAmazonFeeGstCarryForward: number;
  returnedOrderContribution: number;
  expectedReturnCostPerOrder: number;
  expectedContribution: number;
  expectedMargin: number;
  fbaShippingZone: FbaShippingZone | null;
  fbaPackagingWeightKg: number;
  fbaPickPackFee: number;
  fbaStorageCubicFeet: number;
  fbaStorageFee: number;
  fbaInboundCost: number;
}

export interface AdjacentWeightSlabScenario {
  direction: 'previous' | 'next';
  comparisonLabel: string;
  chargeableWeightKg: number;
}

export type DimensionThresholdStatus = 'value' | 'lowest' | 'actualWeightBlocked' | 'heavyBulky';

export interface DimensionThresholdOutcome {
  channel: FulfillmentChannel;
  status: DimensionThresholdStatus;
  value: number | null;
  unit: DimensionUnit;
  resultingSlab: string | null;
  amazonFeeChange: number | null;
  contributionChange: number | null;
  message: string | null;
}

export interface DimensionThresholdResult {
  dimension: PackageDimension;
  previous: {
    easyShip: DimensionThresholdOutcome;
    fba: DimensionThresholdOutcome;
  };
  next: {
    easyShip: DimensionThresholdOutcome;
    fba: DimensionThresholdOutcome;
  };
}

export interface MarginPriceTarget {
  targetMargin: number;
  sellingPrice: number;
  estimate: ContributionEstimate;
}

const isFiniteNumber = (value: number) => Number.isFinite(value);

export function validateContributionInput(input: ContributionInput): string[] {
  const errors: string[] = [];
  const positiveFields: Array<[number, string]> = [
    [input.sellingPrice, 'Selling price'],
    [input.actualWeightGrams, 'Actual packaged weight'],
    [input.lengthCm, 'Package length'],
    [input.widthCm, 'Package width'],
    [input.heightCm, 'Package height'],
  ];
  const nonNegativeFields: Array<[number, string]> = [
    [input.productCost, 'Product cost'],
    [input.packagingCost, 'Packaging cost'],
    [input.advertisingCost, 'Advertising cost'],
    [input.otherCost, 'Other cost'],
    [input.returnRatePercentage, 'Customer return rate'],
    [input.fbaStorageDays, 'FBA average storage days'],
    [input.fbaInboundCost, 'FBA inbound cost'],
  ];

  for (const [value, label] of positiveFields) {
    if (!isFiniteNumber(value) || value <= 0) errors.push(`${label} must be greater than zero.`);
  }
  for (const [value, label] of nonNegativeFields) {
    if (!isFiniteNumber(value) || value < 0) errors.push(`${label} cannot be negative.`);
  }
  if (isFiniteNumber(input.returnRatePercentage) && input.returnRatePercentage > 100) {
    errors.push('Customer return rate cannot exceed 100%.');
  }
  if (
    input.fulfillmentChannel === 'fba' &&
    isFiniteNumber(input.fbaStorageDays) &&
    input.fbaStorageDays > 3650
  ) {
    errors.push('FBA average storage days cannot exceed 3,650 days.');
  }
  return errors;
}

export function calculateVolumetricWeightKg(measurements: PackageMeasurements): number {
  return (measurements.lengthCm * measurements.widthCm * measurements.heightCm) / 5000;
}

export function convertDimension(value: number, from: DimensionUnit, to: DimensionUnit): number {
  if (from === to) return value;
  return from === 'inch' ? value * 2.54 : value / 2.54;
}

export function toCentimeters(value: number, unit: DimensionUnit): number {
  return convertDimension(value, unit, 'cm');
}

export function fromCentimeters(value: number, unit: DimensionUnit): number {
  return convertDimension(value, 'cm', unit);
}

function roundThresholdInUnit(
  exactValueCm: number,
  unit: DimensionUnit,
  direction: 'safe' | 'crossing',
): number {
  const exactValue = fromCentimeters(exactValueCm, unit);
  const roundedDown = Math.floor((exactValue + Number.EPSILON) * 100) / 100;
  return direction === 'safe' ? roundedDown : Math.round((roundedDown + 0.01) * 100) / 100;
}

export function checkHeavyBulky(measurements: PackageMeasurements): HeavyBulkyCheck {
  const dimensions = [measurements.lengthCm, measurements.widthCm, measurements.heightCm].sort(
    (a, b) => b - a,
  );
  const [longest, middle, shortest] = dimensions;
  const girthCm = longest + 2 * (middle + shortest);
  const reasons: string[] = [];

  if (measurements.actualWeightGrams > 22_500) {
    reasons.push('actual package weight is over 22.5 kg');
  }
  if (longest > 183) {
    reasons.push('a package side is over 183 cm');
  }
  if (girthCm > 300) {
    reasons.push('package girth is over 300 cm');
  }

  return { isHeavyBulky: reasons.length > 0, reasons, girthCm };
}

export function calculateEasyShipFee(
  chargeableWeightKg: number,
  stepLevel: StepLevel,
  sellingPrice: number = EASY_SHIP_RATE_CARD.lowPriceMaximum,
): { baseFee: number; discount: number; fee: number; weightSlab: string } {
  if (!isFiniteNumber(chargeableWeightKg) || chargeableWeightKg <= 0) {
    throw new Error('Chargeable weight must be a positive finite number.');
  }
  if (!isFiniteNumber(sellingPrice) || sellingPrice <= 0) {
    throw new Error('Selling price must be a positive finite number.');
  }

  const rate = EASY_SHIP_RATE_CARD.rates[stepLevel];
  const discount =
    sellingPrice <= EASY_SHIP_RATE_CARD.lowPriceMaximum ? EASY_SHIP_RATE_CARD.lowPriceDiscount : 0;
  let baseFee: number;
  let weightSlab: string;

  if (chargeableWeightKg <= 0.5) {
    baseFee = rate.first500g;
    weightSlab = 'First 500 g';
  } else if (chargeableWeightKg <= 1) {
    baseFee = rate.upTo1kg;
    weightSlab = 'Above 500 g to 1 kg';
  } else if (chargeableWeightKg <= 2) {
    baseFee = rate.upTo2kg;
    weightSlab = 'Above 1 kg to 2 kg';
  } else if (chargeableWeightKg <= 5) {
    const additionalKilograms = Math.ceil(chargeableWeightKg - 2);
    baseFee = rate.upTo2kg + additionalKilograms * EASY_SHIP_RATE_CARD.additionalKgAfter2;
    weightSlab = `Above 2 kg to 5 kg (${additionalKilograms} additional kg)`;
  } else {
    const additionalKilogramsAfter5 = Math.ceil(chargeableWeightKg - 5);
    baseFee =
      rate.upTo2kg +
      3 * EASY_SHIP_RATE_CARD.additionalKgAfter2 +
      additionalKilogramsAfter5 * EASY_SHIP_RATE_CARD.additionalKgAfter5;
    weightSlab = `Above 5 kg (${additionalKilogramsAfter5} additional kg after 5 kg)`;
  }

  return { baseFee, discount, fee: baseFee - discount, weightSlab };
}

export function calculateFbaWeightHandlingFee(
  chargeableWeightKg: number,
  stepLevel: StepLevel,
  zone: FbaShippingZone,
): { baseFee: number; discount: number; fee: number; weightSlab: string } {
  if (!isFiniteNumber(chargeableWeightKg) || chargeableWeightKg <= 0) {
    throw new Error('Chargeable weight must be a positive finite number.');
  }

  const rate = FBA_RATE_CARD.rates[stepLevel][zone];
  let fee: number;
  let weightSlab: string;

  if (chargeableWeightKg <= 0.5) {
    fee = rate.first500g;
    weightSlab = 'First 500 g';
  } else if (chargeableWeightKg <= 1) {
    fee = rate.upTo1kg;
    weightSlab = 'Above 500 g to 1 kg';
  } else if (chargeableWeightKg <= 2) {
    fee = rate.upTo2kg;
    weightSlab = 'Above 1 kg to 2 kg';
  } else if (chargeableWeightKg <= 5) {
    const additionalKilograms = Math.ceil(chargeableWeightKg - 2);
    fee = rate.upTo2kg + additionalKilograms * FBA_RATE_CARD.additionalKgAfter2[zone];
    weightSlab = `Above 2 kg to 5 kg (${additionalKilograms} additional kg)`;
  } else {
    const additionalKilogramsAfter5 = Math.ceil(chargeableWeightKg - 5);
    fee =
      rate.upTo2kg +
      3 * FBA_RATE_CARD.additionalKgAfter2[zone] +
      additionalKilogramsAfter5 * FBA_RATE_CARD.additionalKgAfter5[zone];
    weightSlab = `Above 5 kg (${additionalKilogramsAfter5} additional kg after 5 kg)`;
  }

  return { baseFee: fee, discount: 0, fee, weightSlab };
}

export function calculateFbaClosingFee(sellingPrice: number): {
  fee: number;
  priceBand: string;
} {
  if (!isFiniteNumber(sellingPrice) || sellingPrice <= 0) {
    throw new Error('Selling price must be a positive finite number.');
  }
  if (sellingPrice <= 300) return { fee: FBA_RATE_CARD.closing.upTo300, priceBand: '₹0–₹300' };
  if (sellingPrice <= 500) return { fee: FBA_RATE_CARD.closing.upTo500, priceBand: '₹301–₹500' };
  if (sellingPrice <= 1000) {
    return { fee: FBA_RATE_CARD.closing.upTo1000, priceBand: '₹501–₹1,000' };
  }
  return { fee: FBA_RATE_CARD.closing.above1000, priceBand: 'Above ₹1,000' };
}

export function calculateFbaPickPackFee(chargeableWeightKg: number): number {
  if (!isFiniteNumber(chargeableWeightKg) || chargeableWeightKg <= 0) {
    throw new Error('Chargeable weight must be a positive finite number.');
  }
  if (chargeableWeightKg <= 1) return FBA_RATE_CARD.pickPack.upTo1kg;
  if (chargeableWeightKg <= 5) {
    return (
      FBA_RATE_CARD.pickPack.upTo1kg +
      Math.ceil(chargeableWeightKg - 1) * FBA_RATE_CARD.pickPack.additionalKgThrough5
    );
  }
  return (
    FBA_RATE_CARD.pickPack.upTo1kg +
    4 * FBA_RATE_CARD.pickPack.additionalKgThrough5 +
    Math.ceil((chargeableWeightKg - 5) / 5) * FBA_RATE_CARD.pickPack.additional5kgAfter5
  );
}

export function calculateFbaStorageFee(
  measurements: Pick<PackageMeasurements, 'lengthCm' | 'widthCm' | 'heightCm'>,
  storageDays: number,
): { cubicFeet: number; fee: number } {
  if (!isFiniteNumber(storageDays) || storageDays < 0) {
    throw new Error('Storage days must be a non-negative finite number.');
  }
  const cubicFeet =
    (measurements.lengthCm * measurements.widthCm * measurements.heightCm) / 28_316.846_592;
  return {
    cubicFeet,
    fee: cubicFeet * FBA_RATE_CARD.storagePerCubicFootMonth * (storageDays / 30),
  };
}

export function getAdjacentWeightSlabScenarios(chargeableWeightKg: number): {
  previous: AdjacentWeightSlabScenario | null;
  next: AdjacentWeightSlabScenario;
} {
  if (!isFiniteNumber(chargeableWeightKg) || chargeableWeightKg <= 0) {
    throw new Error('Chargeable weight must be a positive finite number.');
  }

  const currentUpperBound =
    chargeableWeightKg <= 0.5
      ? 0.5
      : chargeableWeightKg <= 1
        ? 1
        : chargeableWeightKg <= 2
          ? 2
          : Math.ceil(chargeableWeightKg);
  const previousUpperBound =
    currentUpperBound === 0.5
      ? null
      : currentUpperBound === 1
        ? 0.5
        : currentUpperBound === 2
          ? 1
          : currentUpperBound === 3
            ? 2
            : currentUpperBound - 1;
  const formatWeight = (weightKg: number) =>
    weightKg < 1 ? `${weightKg * 1000} g` : `${weightKg} kg`;

  return {
    previous:
      previousUpperBound === null
        ? null
        : {
            direction: 'previous',
            comparisonLabel: `At or below ${formatWeight(previousUpperBound)}`,
            chargeableWeightKg: previousUpperBound,
          },
    next: {
      direction: 'next',
      comparisonLabel: `Above ${formatWeight(currentUpperBound)}`,
      chargeableWeightKg: currentUpperBound + 0.000_001,
    },
  };
}

export function calculateWallArtReferralFee(sellingPrice: number): {
  fee: number;
  percentage: number;
} {
  if (!isFiniteNumber(sellingPrice) || sellingPrice <= 0) {
    throw new Error('Selling price must be a positive finite number.');
  }

  const rate =
    sellingPrice <= 1000
      ? WALL_ART_FEE_CARD.referral.upTo1000
      : WALL_ART_FEE_CARD.referral.above1000;
  return { fee: sellingPrice * rate, percentage: rate * 100 };
}

export function calculateEasyShipClosingFee(sellingPrice: number): {
  fee: number;
  priceBand: string;
} {
  if (!isFiniteNumber(sellingPrice) || sellingPrice <= 0) {
    throw new Error('Selling price must be a positive finite number.');
  }

  if (sellingPrice <= 300) {
    return { fee: WALL_ART_FEE_CARD.easyShipClosing.upTo300, priceBand: '₹0–₹300' };
  }
  if (sellingPrice <= 500) {
    return { fee: WALL_ART_FEE_CARD.easyShipClosing.upTo500, priceBand: '₹301–₹500' };
  }
  if (sellingPrice <= 1000) {
    return { fee: WALL_ART_FEE_CARD.easyShipClosing.upTo1000, priceBand: '₹501–₹1,000' };
  }
  return { fee: WALL_ART_FEE_CARD.easyShipClosing.above1000, priceBand: 'Above ₹1,000' };
}

export function calculateCustomerReturnFee(
  sellingPrice: number,
  stepLevel: StepLevel,
): { fee: number; priceBand: string } {
  if (!isFiniteNumber(sellingPrice) || sellingPrice <= 0) {
    throw new Error('Selling price must be a positive finite number.');
  }

  const rates = AMAZON_RETURN_FEE_CARD.rates[stepLevel];
  if (sellingPrice <= 300) return { fee: rates.upTo300, priceBand: '₹0–₹300' };
  if (sellingPrice <= 500) return { fee: rates.upTo500, priceBand: '₹301–₹500' };
  if (sellingPrice <= 1000) return { fee: rates.upTo1000, priceBand: '₹501–₹1,000' };
  return { fee: rates.above1000, priceBand: 'Above ₹1,000' };
}

export function calculateContribution(input: ContributionInput): ContributionEstimate {
  const errors = validateContributionInput(input);
  if (errors.length > 0) throw new Error(errors.join(' '));

  const fbaPackagingWeightKg =
    input.fulfillmentChannel === 'fba' && !input.fbaShipInOwnContainer
      ? FBA_RATE_CARD.defaultPackagingWeightGrams / 1000
      : 0;
  const heavyBulky = checkHeavyBulky({
    ...input,
    actualWeightGrams: input.actualWeightGrams + fbaPackagingWeightKg * 1000,
  });
  if (heavyBulky.isHeavyBulky) {
    throw new Error(`Heavy & Bulky shipment: ${heavyBulky.reasons.join(', ')}.`);
  }

  const actualWeightKg = input.actualWeightGrams / 1000 + fbaPackagingWeightKg;
  const volumetricWeightKg = calculateVolumetricWeightKg(input);
  const chargeableWeightKg = Math.max(actualWeightKg, volumetricWeightKg);
  const shipping =
    input.fulfillmentChannel === 'fba'
      ? calculateFbaWeightHandlingFee(chargeableWeightKg, input.stepLevel, input.fbaShippingZone)
      : calculateEasyShipFee(chargeableWeightKg, input.stepLevel, input.sellingPrice);
  const referral = calculateWallArtReferralFee(input.sellingPrice);
  const closing =
    input.fulfillmentChannel === 'fba'
      ? calculateFbaClosingFee(input.sellingPrice)
      : calculateEasyShipClosingFee(input.sellingPrice);
  const fbaPickPackFee =
    input.fulfillmentChannel === 'fba' ? calculateFbaPickPackFee(chargeableWeightKg) : 0;
  const fbaStorage =
    input.fulfillmentChannel === 'fba'
      ? calculateFbaStorageFee(input, input.fbaStorageDays)
      : { cubicFeet: 0, fee: 0 };
  const amazonFeeSubtotal =
    referral.fee + closing.fee + shipping.fee + fbaPickPackFee + fbaStorage.fee;
  const amazonFeeGst = amazonFeeSubtotal * AMAZON_FEE_GST_RATE;
  const amazonCashFees = amazonFeeSubtotal + amazonFeeGst;
  const productGst = input.sellingPrice - input.sellingPrice / (1 + WALL_ART_PRODUCT_GST_RATE);
  const netProductRevenue = input.sellingPrice - productGst;
  const amazonFeeGstUtilized = Math.min(productGst, amazonFeeGst);
  const amazonFeeGstCarryForward = Math.max(amazonFeeGst - productGst, 0);
  const gstPayableAfterAmazonFeeItc = Math.max(productGst - amazonFeeGst, 0);
  const nonAmazonCosts =
    input.productCost +
    input.packagingCost +
    input.advertisingCost +
    input.otherCost +
    (input.fulfillmentChannel === 'fba' ? input.fbaInboundCost : 0);
  const deliveredContribution =
    netProductRevenue - amazonFeeSubtotal - amazonFeeGstCarryForward - nonAmazonCosts;
  const returnFee = calculateCustomerReturnFee(input.sellingPrice, input.stepLevel);
  const returnedOrderAmazonFeeSubtotal =
    shipping.fee + closing.fee + returnFee.fee + fbaPickPackFee + fbaStorage.fee;
  const returnedOrderAmazonFeeGst = returnedOrderAmazonFeeSubtotal * AMAZON_FEE_GST_RATE;
  const returnedOrderAmazonFees = returnedOrderAmazonFeeSubtotal + returnedOrderAmazonFeeGst;
  const returnedOrderAmazonFeeGstCarryForward = returnedOrderAmazonFeeGst;
  const returnedOrderContribution =
    -returnedOrderAmazonFees -
    input.packagingCost -
    input.advertisingCost -
    input.otherCost -
    (input.fulfillmentChannel === 'fba' ? input.fbaInboundCost : 0);
  const returnRate = input.returnRatePercentage / 100;
  const expectedContribution =
    deliveredContribution * (1 - returnRate) + returnedOrderContribution * returnRate;
  const expectedReturnCostPerOrder =
    (deliveredContribution - returnedOrderContribution) * returnRate;

  return {
    fulfillmentChannel: input.fulfillmentChannel,
    actualWeightKg,
    volumetricWeightKg,
    chargeableWeightKg,
    girthCm: heavyBulky.girthCm,
    weightSlab: shipping.weightSlab,
    easyShipBaseFee: shipping.baseFee,
    easyShipDiscount: shipping.discount,
    easyShipFee: shipping.fee,
    referralPercentage: referral.percentage,
    referralFee: referral.fee,
    closingFeeBand: closing.priceBand,
    closingFee: closing.fee,
    amazonFeeSubtotal,
    amazonFeeGst,
    amazonCashFees,
    amazonFeeGstUtilized,
    amazonFeeGstCarryForward,
    productGst,
    netProductRevenue,
    gstPayableAfterAmazonFeeItc,
    nonAmazonCosts,
    deliveredContribution,
    deliveredMargin: (deliveredContribution / netProductRevenue) * 100,
    returnRatePercentage: input.returnRatePercentage,
    refundFeeBand: returnFee.priceBand,
    refundFee: returnFee.fee,
    returnedOrderAmazonFeeSubtotal,
    returnedOrderAmazonFeeGst,
    returnedOrderAmazonFees,
    returnedOrderAmazonFeeGstCarryForward,
    returnedOrderContribution,
    expectedReturnCostPerOrder,
    expectedContribution,
    expectedMargin: (expectedContribution / netProductRevenue) * 100,
    fbaShippingZone: input.fulfillmentChannel === 'fba' ? input.fbaShippingZone : null,
    fbaPackagingWeightKg,
    fbaPickPackFee,
    fbaStorageCubicFeet: fbaStorage.cubicFeet,
    fbaStorageFee: fbaStorage.fee,
    fbaInboundCost: input.fulfillmentChannel === 'fba' ? input.fbaInboundCost : 0,
  };
}

function getWeightSlabBounds(chargeableWeightKg: number): {
  previousUpperBound: number | null;
  currentUpperBound: number;
} {
  const currentUpperBound =
    chargeableWeightKg <= 0.5
      ? 0.5
      : chargeableWeightKg <= 1
        ? 1
        : chargeableWeightKg <= 2
          ? 2
          : Math.ceil(chargeableWeightKg);

  return {
    currentUpperBound,
    previousUpperBound:
      currentUpperBound === 0.5
        ? null
        : currentUpperBound === 1
          ? 0.5
          : currentUpperBound === 2
            ? 1
            : currentUpperBound === 3
              ? 2
              : currentUpperBound - 1,
  };
}

function calculateMethodDimensionThresholds(
  input: ContributionInput,
  dimension: PackageDimension,
  unit: DimensionUnit,
): {
  previous: DimensionThresholdOutcome;
  next: DimensionThresholdOutcome;
} {
  const current = calculateContribution(input);
  const { previousUpperBound, currentUpperBound } = getWeightSlabBounds(current.chargeableWeightKg);
  const otherDimensions = (['lengthCm', 'widthCm', 'heightCm'] as const).filter(
    (key) => key !== dimension,
  );
  const otherDimensionProduct = input[otherDimensions[0]] * input[otherDimensions[1]];
  const actualWeightKg =
    input.actualWeightGrams / 1000 +
    (input.fulfillmentChannel === 'fba' && !input.fbaShipInOwnContainer
      ? FBA_RATE_CARD.defaultPackagingWeightGrams / 1000
      : 0);

  const makeValueOutcome = (
    targetWeightKg: number,
    direction: 'safe' | 'crossing',
  ): DimensionThresholdOutcome => {
    const exactValueCm = (targetWeightKg * 5000) / otherDimensionProduct;
    const value = roundThresholdInUnit(exactValueCm, unit, direction);
    const candidateInput = {
      ...input,
      [dimension]: toCentimeters(value, unit),
    };
    const heavyBulky = checkHeavyBulky({
      ...candidateInput,
      actualWeightGrams:
        candidateInput.actualWeightGrams +
        (candidateInput.fulfillmentChannel === 'fba' && !candidateInput.fbaShipInOwnContainer
          ? FBA_RATE_CARD.defaultPackagingWeightGrams
          : 0),
    });

    if (heavyBulky.isHeavyBulky) {
      return {
        channel: input.fulfillmentChannel,
        status: 'heavyBulky',
        value: null,
        unit,
        resultingSlab: null,
        amazonFeeChange: null,
        contributionChange: null,
        message: `Heavy & Bulky at this threshold: ${heavyBulky.reasons.join(', ')}.`,
      };
    }

    const candidate = calculateContribution(candidateInput);
    return {
      channel: input.fulfillmentChannel,
      status: 'value',
      value,
      unit,
      resultingSlab: candidate.weightSlab,
      amazonFeeChange: candidate.amazonCashFees - current.amazonCashFees,
      contributionChange: candidate.expectedContribution - current.expectedContribution,
      message: null,
    };
  };

  let previous: DimensionThresholdOutcome;
  if (previousUpperBound === null) {
    previous = {
      channel: input.fulfillmentChannel,
      status: 'lowest',
      value: null,
      unit,
      resultingSlab: null,
      amazonFeeChange: null,
      contributionChange: null,
      message: 'Already in lowest slab.',
    };
  } else if (actualWeightKg > previousUpperBound) {
    previous = {
      channel: input.fulfillmentChannel,
      status: 'actualWeightBlocked',
      value: null,
      unit,
      resultingSlab: null,
      amazonFeeChange: null,
      contributionChange: null,
      message: 'Actual weight prevents a lower slab.',
    };
  } else {
    previous = makeValueOutcome(previousUpperBound, 'safe');
  }

  return {
    previous,
    next: makeValueOutcome(currentUpperBound, 'crossing'),
  };
}

export function calculateDimensionThresholds(
  input: ContributionInput,
  dimension: PackageDimension,
  unit: DimensionUnit,
): DimensionThresholdResult {
  const easyShip = calculateMethodDimensionThresholds(
    { ...input, fulfillmentChannel: 'easyShip' },
    dimension,
    unit,
  );
  const fba = calculateMethodDimensionThresholds(
    { ...input, fulfillmentChannel: 'fba' },
    dimension,
    unit,
  );

  return {
    dimension,
    previous: { easyShip: easyShip.previous, fba: fba.previous },
    next: { easyShip: easyShip.next, fba: fba.next },
  };
}

const SELLING_PRICE_SEARCH_BANDS = [
  { minimum: 1, maximum: 300 },
  { minimum: 301, maximum: 500 },
  { minimum: 501, maximum: 1000 },
  { minimum: 1001, maximum: 10_000_000 },
] as const;

export function calculateMarginPriceTarget(
  input: ContributionInput,
  targetMargin: number,
): MarginPriceTarget | null {
  if (!isFiniteNumber(targetMargin) || targetMargin < 0 || targetMargin >= 100) {
    throw new Error(
      'Target margin must be a finite percentage from 0 up to, but not including, 100.',
    );
  }

  const estimateAt = (sellingPrice: number) => calculateContribution({ ...input, sellingPrice });

  for (const band of SELLING_PRICE_SEARCH_BANDS) {
    const maximumEstimate = estimateAt(band.maximum);
    if (maximumEstimate.expectedMargin + Number.EPSILON < targetMargin) continue;

    let low: number = band.minimum;
    let high: number = band.maximum;
    while (low < high) {
      const midpoint = Math.floor((low + high) / 2);
      const midpointEstimate = estimateAt(midpoint);
      if (midpointEstimate.expectedMargin >= targetMargin) {
        high = midpoint;
      } else {
        low = midpoint + 1;
      }
    }

    const estimate = estimateAt(low);
    if (estimate.expectedMargin >= targetMargin) {
      return { targetMargin, sellingPrice: low, estimate };
    }
  }

  return null;
}
